/**
 * A captured start (10 §2, mission 32): the ranger begins in a cell, not in the drop band, and his pods
 * already lie on the ground. What the drop-band gates promise a parachutist has to hold here instead:
 * nobody is looking at him when the mission starts, his pods lie on good ground, and the cell, the pods
 * and the pickup are all joined on foot without crossing a mine or forcing wire.
 */

import { segmentHit } from '../../sim/statics';
import { ENEMY } from '../../sim/tuning';
import type { MissionDef } from '../../sim/types';
import { QUIET } from '../tuning';
import type { NavProbe } from './nav';

/** A standing man's height, as `perceive` has it (src/sim/enemies/perception.ts). */
const STANDING = 1.5;

const cellOf = (nav: NavProbe, x: number, z: number): number => {
  const cx = Math.floor(x);
  const cz = Math.floor(z);
  return cx < 0 || cz < 0 || cx >= nav.width || cz >= nav.height ? -1 : cz * nav.width + cx;
};

/** Could a soldier at (x, z) see a standing man at the start, whichever way he happens to be facing? */
function sees(nav: NavProbe, def: MissionDef, x: number, z: number, ignoreProp: number): boolean {
  const { x: px, z: pz } = def.player;
  const range = ENEMY.range[def.timeOfDay] * 1.1;
  if ((px - x) * (px - x) + (pz - z) * (pz - z) > range * range) return false;
  for (const c of nav.colliders) {
    if (!c.blocksSight || c.propId === ignoreProp) continue;
    const hit = segmentHit(c, x, z, px, pz);
    if (!hit) continue;
    const drop = STANDING - QUIET.eye;
    if (Math.min(QUIET.eye + drop * hit.enter, QUIET.eye + drop * hit.exit) < c.h) return false;
  }
  return true;
}

export function checkCaptured(def: MissionDef, nav: NavProbe): string[] {
  if (def.startCaptured !== true) return [];
  const out: string[] = [];
  const pods = def.podsAt ?? [];
  if (pods.length === 0) out.push('a captured start needs pods on the ground: podsAt is empty');

  const start = cellOf(nav, def.player.x, def.player.z);
  if (start >= 0 && nav.cost[start] !== 0) {
    out.push('the cell cannot be walked out of to the pickup without crossing mines or wire');
  }
  pods.forEach((pod, i) => {
    const letter = 'ABC'[i] ?? String(i);
    const cell = cellOf(nav, pod.x, pod.z);
    if (cell < 0) out.push(`pod ${letter} lies off the map`);
    else if (nav.full.blocked[cell] !== 0 || nav.soft[cell] !== 0 || nav.costly[cell] !== 0) {
      out.push(`pod ${letter} lies on bad ground at ${pod.x}, ${pod.z}: something solid, water, a depression or mines`);
    } else if (nav.cost[cell] !== 0) {
      out.push(`pod ${letter} at ${pod.x}, ${pod.z} cannot be walked to without crossing mines or wire`);
    }
  });

  // The mission must not open with a guard looking straight at him: men who walk a beat count where they start.
  const watcher = def.spawns.find((s) => s.dormantUntil === undefined && s.kind !== 'minitank' && s.kind !== 'miniBunker'
    && sees(nav, def, s.x, s.z, s.mans ?? -1));
  if (watcher) out.push(`the ${watcher.kind} at ${watcher.x}, ${watcher.z} can see the ranger in his cell`);
  return out;
}
